import type { IGeoFilter, Targetable } from '@feathr/blackbox';

import type {
  GeoFilterModel,
  IControlledProps,
  IProps,
  IUncontrolledProps,
} from './GeoFilter.hooks';

/**
 * Option for the geo filter type dropdown.
 */
export interface ITypeOption {
  id: string;
  name: string;
}

/**
 * Available geo filter type options for filtering search results.
 */
export const TYPE_OPTIONS: ITypeOption[] = [
  { id: 'all', name: 'All types' },
  { id: 'Country', name: 'Countries' },
  { id: 'Region', name: 'Regions' },
  { id: 'City', name: 'Cities' },
  { id: 'Zip', name: 'Zip Codes' },
];

/**
 * Type guard to check if props are for controlled mode.
 *
 * @param props - The component props
 * @returns True if the component is in controlled mode
 */
export function isControlled(props: IProps): props is IControlledProps {
  return 'geoFilterId' in props;
}

/**
 * Converts a GeoFilterModel to a plain IGeoFilter object for use in the select.
 *
 * @param geofilter - The geo filter model (may be undefined or pending)
 * @returns The plain object representation, or undefined if not ready
 */
export function getGeoFilterValue(geofilter: GeoFilterModel | undefined): IGeoFilter | undefined {
  if (!geofilter) {
    return undefined;
  }
  if (geofilter.isPending) {
    return undefined;
  }
  return geofilter.toJS();
}

/**
 * Gets the current geo filter ID from props, handling both controlled and uncontrolled modes.
 *
 * @param props - The component props
 * @param geofilter - The current geo filter model (for uncontrolled mode)
 * @returns The current geo filter ID, or undefined
 */
export function getCurrentGeoFilterId(
  props: IProps,
  geofilter: GeoFilterModel | undefined,
): string | undefined {
  if (isControlled(props)) {
    return (props as IControlledProps).geoFilterId;
  }
  return geofilter?.id;
}

/**
 * Gets the set of already-used geo filter IDs to exclude from search results.
 *
 * @param props - The component props
 * @param getTargetable - Function to get a targetable by ID
 * @returns Set of used geo filter IDs
 */
export function getUsedGeoFilterIds(
  props: IProps,
  getTargetable: (id: string) => Targetable | undefined,
): Set<string> {
  if (isControlled(props)) {
    return (props as IControlledProps).usedGeoFilterIds ?? new Set<string>();
  }

  const uncontrolledProps = props as IUncontrolledProps;
  const usedIds = new Set<string>();

  uncontrolledProps.targetings.forEach((t) => {
    const targetableId = t.get('target_data');
    if (targetableId) {
      const targetable = getTargetable(targetableId);
      const geoFilterId = targetable?.get('geo_filter');
      if (geoFilterId) {
        usedIds.add(geoFilterId);
      }
    }
  });

  return usedIds;
}

/**
 * Gets the current included value from props, handling both controlled and uncontrolled modes.
 *
 * @param props - The component props
 * @returns Whether the filter is included (true) or excluded (false)
 */
export function getIncludedValue(props: IProps): boolean {
  if (isControlled(props)) {
    return (props as IControlledProps).included;
  }
  return (props as IUncontrolledProps).targeting.get('included');
}

/**
 * Generates a cache key for the async select based on the current state.
 * Including the selected type causes the AsyncSelect to remount when the
 * type filter changes, which invalidates react-select's option cache so the
 * dropdown re-fetches with the new `kind__in` filter applied.
 *
 * @param props - The component props
 * @param selectedTypeId - The id of the currently selected type option
 * @returns A unique cache key string
 */
export function getCacheKey(props: IProps, selectedTypeId: string): string {
  if (isControlled(props)) {
    const id = props.geoFilterId ?? 'new';
    return `geo-filter-controlled-${id}-${selectedTypeId}`;
  }
  return `geo-filter-${props.targeting.id}-${selectedTypeId}`;
}

/**
 * Returns true if the given string would be coerced by JSON.parse into a
 * non-string value (number, boolean, null, array, object). Used to decide
 * whether to pre-quote a filter value so the backend's value coercion
 * doesn't misread values such as "32601" as the number 32601 or "true" as
 * the boolean.
 *
 * @param value - The string to test
 * @returns True when the value must be JSON.stringify'd before sending
 */
export function needsQuoting(value: string): boolean {
  try {
    return typeof JSON.parse(value) !== 'string';
  } catch {
    return false;
  }
}
